import TopSection from './top-section';
import React, { ReactNode, useMemo } from 'react';
import { DEFAULT_BORDER, isPortrait, useRpx } from '../shared';
import Circles from './circles';
import TopGrid from './top-grid';
import Media from '../../../../jordan/components/Media';
import { useNbyStore } from '../store';

interface LeftGridProps {
  content: ReactNode;
  description?: ReactNode;
  extended?: boolean;
  hideBottom?: boolean;
  contentHeight?: number;
  withoutRoundCorners?: boolean;
  theme?: string;
}

export default function LeftGrid(props: LeftGridProps) {
  const {
    content,
    contentHeight,
    description,
    extended,
    hideBottom,
    withoutRoundCorners,
  } = props;
  const animationData = useNbyStore(state => state.animationData);

  const rpx = useRpx();
  const portrait = isPortrait();

  const logo = useMemo(() => {
    if (animationData?.logo) {
      return (
        <Media
          media={[animationData?.logo]}
          cover={'fit'}
          width={'100%'}
          transparent
          height={portrait ? 120 : rpx(115)}
          mediaStyle={{
            objectPosition: 'left',
          }}
          style={{
            marginLeft: portrait ? 55 : rpx(50),
          }}
        />
      );
    }


    return props.theme === 'one' ? <OneLogo /> : <Logo />;
  }, [animationData]);

  return (
    <div
      style={{
        height: !portrait && '100%',
        position: 'relative',
      }}
    >
      <TopSection>
        <>
          <div
            style={{
              display: 'flex',
              width: '100%',
            }}
          >
            <div
              style={{
                borderRight: props.theme === 'one' ? undefined : DEFAULT_BORDER,
                height: '100%',
                width: portrait ? 432 : '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              {logo}
            </div>
            {
              animationData?.lockup && (
                <div
                  style={{ marginRight: 30, width: '100%', height: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                  <Media
                    style={{
                      background: 'black',
                    }}
                    transparent
                    media={[animationData?.lockup]}
                    cover={'fit'}
                    width={'75%'}
                    height={'100%'}
                    preventExit
                    preventInitial
                    preventTransitions
                    preventInitialSlide
                  />
                </div>

              )
            }
          </div>
          <div style={{ height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'flex-end' }}>
            {
              props.theme !== 'one' && (
                <div style={{ transform: 'rotate(180deg)' }}>
                  {
                    portrait ? <TopGrid withoutRoundCorners={withoutRoundCorners} /> : <Circles />
                  }
                </div>
              )
            }
          </div>
        </>
      </TopSection>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          position: 'relative',
        }}
      >
        <div
          style={{
            height: contentHeight || rpx(extended ? 590 : 480),
            width: '100%',
            borderBottom: !hideBottom && DEFAULT_BORDER,
            flexShrink: 0,
          }}
        >
          {content}
        </div>

        {
          hideBottom ? null : (
            <div
              style={{
                height: '50%',
                display: 'flex',
              }}
            >
              <div
                style={{
                  width: '50%',
                  borderRight: DEFAULT_BORDER,
                }}
              >
                <Circles />
              </div>
              <div
                style={{
                  width: '50%',
                  position: 'relative',
                }}
              >
                {description}
              </div>
            </div>
          )
        }
      </div>
    </div>
  );
}

export function OneLogo() {
  const rpx = useRpx();
  const portrait = isPortrait();

  return (
    <div
      style={{
        height: portrait ? 120 : rpx(115),
        width: '100%',
        marginLeft: portrait ? 34 : rpx(50),
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="207"
        height="120"
        fill="none"
        viewBox="0 0 207 120"
      >
        <path
          fill="currentColor"
          d="M.728 55V.712h13.968l7.632 19.944c1.056 2.832 2.376 7.104 3.96 12.816-.144-1.584-.36-3.864-.648-6.84-.24-3.024-.432-5.472-.576-7.344a95.18 95.18 0 01-.144-4.752V.712h14.04V55H24.92l-7.344-19.944c-1.68-4.512-2.928-8.304-3.744-11.376.576 7.584.864 13.416.864 17.496V55H.728zm41.695 0V.712H57.04V55H42.423zm18.07 0V.712H75.11v18.432c0 1.392-.12 3-.36 4.824l9-23.256h15.12L87.134 26.92 100.31 55H84.83L74.75 29.944c.24 2.16.36 4.032.36 5.616V55H60.494zm40.219 0V.712h27.216v12.744h-12.6v7.92h11.304v12.312h-11.304v8.28h12.6V55h-27.216zM.728 119V64.712h17.064c6.24 0 10.92 1.392 14.04 4.176 3.168 2.784 4.752 6.528 4.752 11.232 0 6.768-3.36 10.44-10.08 11.016 3.888.576 6.696 1.848 8.424 3.816 1.776 1.92 2.664 4.752 2.664 8.496 0 4.608-1.512 8.352-4.536 11.232-3.024 2.88-6.96 4.32-11.808 4.32H.728zm14.616-11.808c.384.048.816.072 1.296.072 1.824 0 3.264-.456 4.32-1.368 1.104-.96 1.656-2.256 1.656-3.888 0-1.632-.552-2.904-1.656-3.816-1.056-.912-2.544-1.368-4.464-1.368h-1.152v10.368zm0-21.384h1.008c1.776 0 3.168-.408 4.176-1.224 1.008-.816 1.512-1.968 1.512-3.456 0-3.12-1.728-4.68-5.184-4.68-.48 0-.984.024-1.512.072v9.288zm18.712-21.096H49.32l2.664 8.064c.864 2.544 1.704 5.448 2.52 8.712.912-3.744 1.728-6.648 2.448-8.712l2.664-8.064h15.192l-12.6 31.32V119H47.52V96.032l-13.464-31.32zm50.835 0h15.265l2.664 8.064c.864 2.544 1.704 5.448 2.52 8.712.912-3.744 1.728-6.648 2.448-8.712l2.664-8.064h15.192l-12.6 31.32V119H98.356V96.032L84.89 64.712zm40.556 27.144c0-8.352 1.896-15.12 5.688-20.304 3.792-5.232 8.784-7.848 14.976-7.848s11.184 2.616 14.976 7.848c3.792 5.184 5.688 11.952 5.688 20.304 0 8.4-1.896 15.192-5.688 20.376-3.792 5.136-8.784 7.704-14.976 7.704s-11.184-2.568-14.976-7.704c-3.792-5.184-5.688-11.976-5.688-20.376zM142.079 81.2c-.96 2.496-1.44 6.048-1.44 10.656 0 4.608.48 8.16 1.44 10.656.96 2.496 2.304 3.744 4.032 3.744 1.728 0 3.072-1.248 4.032-3.744.96-2.496 1.44-6.048 1.44-10.656 0-4.608-.48-8.16-1.44-10.656-.96-2.544-2.304-3.816-4.032-3.816-1.728 0-3.072 1.272-4.032 3.816zm27.253 19.8V64.712h14.688v34.992c0 2.4.288 4.104.864 5.112.576.96 1.56 1.44 2.952 1.44s2.352-.48 2.88-1.44.792-2.664.792-5.112V64.712h14.688V101c0 6.048-1.656 10.728-4.968 14.04-3.312 3.264-7.776 4.896-13.392 4.896s-10.104-1.632-13.464-4.896c-3.36-3.312-5.04-7.992-5.04-14.04z"
        ></path>
      </svg>
    </div>
  );
}

function Logo() {
  const rpx = useRpx();
  const portrait = isPortrait();

  return (
    <div
      style={{
        height: portrait ? 120 : rpx(115),
        width: '100%',
        marginLeft: portrait ? 34 : rpx(50),
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        style={{
          width: 'auto',
          height: '100%',
          // objectFit: 'contain',
        }}
        // width="201"
        // height="117"
        fill="none"
        viewBox="0 0 204 120"
      >
        <path fillRule="evenodd" clipRule="evenodd"
              d="M25.2625 32.7676L23.1493 25.767C22.8346 24.7245 21.902 22.1159 21.5155 21.0875L21.5146 21.0849L14.1252 1.20658H0.781777V55.3456H14.099V41.6143C14.099 39.4436 14.0065 32.5147 13.9161 31.0696L13.5218 24.7688L15.4411 30.7831C15.7347 31.7031 17.9918 37.9612 20.1876 44.0216C21.2829 47.0447 22.3595 50.0091 23.1623 52.2178C23.5637 53.3221 23.8966 54.2375 24.1292 54.8768L24.3068 55.3649H37.7293V1.22584H24.3015V14.9475C24.3015 17.1069 24.5683 24.1256 24.677 25.4785L25.2625 32.7676ZM23.5197 0.444062H38.5111V56.1467H23.7594C23.7594 56.1467 17.8502 39.9155 15.5573 33.4821C15.1061 32.2161 14.7949 31.3296 14.6963 31.0208C14.7246 31.4726 14.7529 32.4394 14.7785 33.6255C14.8364 36.308 14.8807 40.1118 14.8807 41.6143V56.1274H0L0 0.424805H14.6687L22.2473 20.8125C22.4166 21.2629 22.6916 22.0188 22.9744 22.8163C23.3387 23.8433 23.716 24.9393 23.8977 25.5411C23.8608 25.082 23.8065 23.9936 23.7505 22.6658C23.6385 20.0107 23.5197 16.3985 23.5197 14.9475V0.444062Z"
              fill="currentColor" />
        <path fillRule="evenodd" clipRule="evenodd"
              d="M56.8358 1.22514H42.9009V55.3449H56.8358V1.22514ZM57.6176 0.443359V56.1267H42.1191V0.443359H57.6176Z"
              fill="currentColor" />
        <path fillRule="evenodd"
              d="M84.6944 56.3099H101.631L88.167 27.616L100.184 0.803711H83.6158L76.0367 18.3618L76.1124 0.803711H60.1973V56.3099H76.1124L76.0272 36.755L84.6944 56.3099ZM75.3272 55.5281L75.2292 33.0252L85.203 55.5281H100.401L87.3069 27.6235L98.9767 1.58549H84.1298L75.2384 22.1837L75.3272 1.58549H60.979V55.5281H75.3272Z"
              fill="currentColor" />
        <path fillRule="evenodd" clipRule="evenodd"
              d="M117.013 42.7495V34.2645H128.458V22.2731H117.013V14.0761H129.775V1.58549H102.413V55.5281H129.775V42.7495H117.013ZM130.557 41.9677V56.3099H101.631V0.803711H130.557V14.8578H117.795V21.4913H129.24V35.0463H117.795V41.9677H130.557Z"
              fill="currentColor" />
        <path fillRule="evenodd" clipRule="evenodd"
              d="M15.8304 83.6972H15.4985V75.675H16.319C19.3155 75.675 20.7169 76.9462 20.7169 79.6813C20.7426 80.2294 20.6451 80.7762 20.4321 81.2779C20.2192 81.7796 19.8968 82.2222 19.4906 82.5704L19.9995 83.1639C20.4989 82.7357 20.8926 82.1938 21.1518 81.5833C21.4084 80.9786 21.5267 80.3216 21.4986 79.6634C21.4956 78.1779 21.1104 76.9321 20.1678 76.0743C19.237 75.2272 17.9076 74.8932 16.319 74.8932H14.7167V84.4789H15.8049C17.293 84.5731 18.7647 84.1379 19.9797 83.2497L19.5183 82.6185C18.442 83.4053 17.1413 83.7858 15.8304 83.6972ZM20.4339 104.476L20.4173 104.489C19.1884 105.489 17.6495 105.995 16.0842 105.907H14.7167V95.2424H15.9527C17.5508 95.1424 19.1283 95.6314 20.41 96.6181L20.4328 96.6356L20.4543 96.6548C20.9977 97.1407 21.4229 97.7495 21.6989 98.4314C21.9692 99.0992 22.0898 99.821 22.0529 100.543C22.087 101.269 21.9646 101.995 21.6938 102.667C21.4172 103.354 20.9925 103.968 20.4497 104.461L20.4339 104.476ZM21.2701 100.541C21.3075 99.9197 21.2062 99.2979 20.9742 98.7247C20.7422 98.1514 20.3858 97.6423 19.9332 97.2376C18.7925 96.3595 17.3919 95.9299 15.9779 96.0242H15.4985V105.125H16.107C17.4814 105.208 18.8378 104.766 19.9239 103.883C20.3779 103.47 20.7353 102.954 20.9686 102.375C21.202 101.796 21.3051 101.168 21.2701 100.541ZM26.8428 89.8068L29.1527 88.9976C31.2765 88.2537 32.8097 87.0691 33.8249 85.4143C34.8484 83.7457 35.3875 81.5301 35.3875 78.6604V78.6542C35.4042 76.5561 35.0362 74.4748 34.3039 72.5228C33.5935 70.6508 32.4664 68.9865 31.0192 67.6669C29.366 66.2124 27.4434 65.1345 25.3741 64.4999L25.3721 64.4993C22.7589 63.69 20.0434 63.3024 17.3191 63.3496L17.3123 63.3497H0.781777V117.469H20.5956C22.7699 117.491 24.9314 117.114 26.9818 116.353L26.9836 116.352C28.8272 115.674 30.5217 114.61 31.9634 113.223C33.3562 111.848 34.4574 110.179 35.1929 108.325C35.9763 106.323 36.3687 104.177 36.3463 102.013L36.3463 102.005C36.3463 98.216 35.4501 95.4069 33.7783 93.5297L33.8149 93.4971C32.5812 92.0453 30.9756 91.0015 29.194 90.4865L26.8428 89.8068ZM34.3621 93.0098C34.3714 93.0202 34.3806 93.0306 34.3898 93.041C36.2153 95.1107 37.1281 98.1239 37.1281 102.005C37.1515 104.269 36.7409 106.515 35.9203 108.611C35.1449 110.567 33.9824 112.329 32.509 113.783C30.9899 115.245 29.2019 116.369 27.2537 117.086C25.1143 117.88 22.858 118.274 20.5878 118.251H0L0 62.5679H17.3055C20.1131 62.5193 22.9112 62.9188 25.6033 63.7525C27.7824 64.4208 29.8045 65.5556 31.5409 67.0846C33.0832 68.4894 34.2814 70.2593 35.0352 72.2465C35.8018 74.2893 36.1868 76.4664 36.1692 78.6604C36.1692 84.0778 34.3102 87.5212 30.5217 89.2854C30.1683 89.4499 29.7981 89.5999 29.4111 89.7355C29.7949 89.8464 30.171 89.9799 30.5376 90.1349C32.0118 90.7581 33.3344 91.7287 34.3989 92.9771C34.4082 92.988 34.4174 92.9989 34.4267 93.0098H34.3621Z"
              fill="currentColor" />
        <path fillRule="evenodd" clipRule="evenodd"
              d="M105.629 76.0492C105.615 75.9963 105.602 75.9434 105.589 75.8903C105.183 74.4169 104.722 72.8856 104.206 71.3544L101.44 62.8218H85.2129L98.6369 94.9875V118.515H114.209V94.9779L126.776 62.8218H110.586L107.82 71.3447C107.303 72.8952 106.852 74.4265 106.474 75.9C106.461 75.9494 106.448 75.9994 106.436 76.0488C106.301 76.5769 106.167 77.1036 106.04 77.6142C106.002 77.4702 105.964 77.325 105.925 77.1786C105.826 76.8084 105.726 76.431 105.629 76.0492ZM107.231 76.0942C107.083 76.6723 106.937 77.247 106.799 77.8021L106.065 80.7684L105.285 77.8138C105.247 77.6726 105.209 77.5291 105.17 77.3838C105.059 76.9637 104.943 76.5282 104.833 76.0893C104.431 74.6307 103.975 73.1167 103.465 71.6042L103.462 71.5955L100.871 63.6036H86.3863L99.4187 94.8309V117.733H113.427V94.8306L125.631 63.6036H111.154L108.562 71.5917C108.051 73.1258 107.604 74.6394 107.231 76.0942Z"
              fill="currentColor" />
        <path fillRule="evenodd" clipRule="evenodd"
              d="M52.6192 71.0966L49.8532 62.564H33.6172L47.0504 94.7297V118.257H62.6134V94.7201L75.18 62.564H58.99L56.2241 71.0869C55.7078 72.6374 55.256 74.1687 54.8872 75.6421C54.8739 75.6946 54.8603 75.7476 54.8469 75.8C54.7129 76.325 54.5796 76.8487 54.4539 77.3564L53.9929 75.6325C53.5872 74.159 53.1262 72.6278 52.6192 71.0966ZM59.5582 63.3457L56.9658 71.3339C56.455 72.8681 56.009 74.3802 55.6456 75.832L55.6447 75.8355C55.4971 76.4139 55.3503 76.9889 55.2128 77.5442L54.4831 80.4915L53.2392 75.84C53.2389 75.8391 53.2387 75.8382 53.2384 75.8373C52.8363 74.377 52.3794 72.8594 51.877 71.3423L51.8755 71.3376L49.2848 63.3457H34.7909L47.8322 94.573V117.475H61.8316V94.5727L74.0351 63.3457H59.5582Z"
              fill="currentColor" />
        <path fillRule="evenodd" clipRule="evenodd"
              d="M138.679 79.9041L138.68 79.9015C139.543 77.6041 140.806 76.0721 142.731 76.0721C144.655 76.0721 145.927 77.6001 146.782 79.9005C147.755 82.4443 148.188 86.0935 148.188 90.6736C148.188 95.2601 147.735 98.8878 146.782 101.409C145.924 103.689 144.648 105.188 142.731 105.188C140.813 105.188 139.548 103.687 138.682 101.412C137.708 98.8864 137.275 95.2449 137.275 90.6736C137.275 86.0943 137.737 82.441 138.679 79.9041ZM146.05 101.132C145.221 103.338 144.133 104.407 142.731 104.407C141.33 104.407 140.251 103.338 139.412 101.132C138.49 98.7439 138.057 95.2191 138.057 90.6736C138.057 86.128 138.518 82.584 139.412 80.1763C140.251 77.9421 141.33 76.8538 142.731 76.8538C144.133 76.8538 145.221 77.9421 146.05 80.1763C146.972 82.584 147.406 86.1183 147.406 90.6736C147.406 95.2288 146.954 98.7439 146.05 101.132ZM157.806 69.8718C156.062 67.3989 153.813 65.3625 151.223 63.9105C148.586 62.5114 145.67 61.7822 142.713 61.7822C139.755 61.7822 136.84 62.5114 134.203 63.9105C131.627 65.3693 129.392 67.405 127.657 69.8718C125.743 72.6189 124.322 75.7056 123.462 78.9822C121.507 86.6133 121.507 94.6471 123.462 102.278C124.322 105.558 125.743 108.648 127.657 111.398C129.389 113.862 131.629 115.886 134.212 117.321C136.854 118.696 139.766 119.411 142.718 119.411C145.669 119.411 148.581 118.696 151.223 117.321C153.816 115.89 156.066 113.865 157.806 111.398C159.724 108.65 161.146 105.56 162.001 102.278C163.955 94.6471 163.955 86.6133 162.001 78.9822C161.145 75.7041 159.723 72.6167 157.806 69.8718ZM161.244 79.1781C163.165 86.6809 163.165 94.5799 161.244 102.083C160.412 105.274 159.029 108.279 157.166 110.949C155.495 113.318 153.337 115.259 150.853 116.632C148.324 117.946 145.539 118.629 142.718 118.629C139.896 118.629 137.112 117.947 134.583 116.632C132.109 115.257 129.961 113.315 128.298 110.95C126.438 108.277 125.056 105.273 124.219 102.082C122.298 94.5796 122.298 86.6809 124.219 79.1784C125.056 75.991 126.438 72.9899 128.297 70.32C129.965 67.9498 132.11 65.9961 134.579 64.5961C137.101 63.2596 139.888 62.564 142.713 62.564C145.538 62.564 148.325 63.2598 150.848 64.5968C153.332 65.9902 155.49 67.9447 157.166 70.321C159.029 72.9883 160.411 75.9897 161.244 79.1781Z"
              fill="currentColor" />
        <path fillRule="evenodd" clipRule="evenodd"
              d="M188.421 103.628L188.386 103.688C188.08 104.203 187.637 104.621 187.104 104.887C186.599 105.14 186.037 105.246 185.477 105.192C184.909 105.241 184.339 105.133 183.824 104.881C183.281 104.616 182.824 104.202 182.498 103.688L182.46 103.628L182.434 103.562C181.788 101.961 181.513 100.225 181.63 98.495V63.6036H167.649V99.8291L167.649 99.8362C167.598 102.628 168.049 105.405 168.977 108.023C169.781 110.228 171.025 112.226 172.625 113.884C174.226 115.485 176.139 116.702 178.23 117.451C180.568 118.283 183.024 118.694 185.492 118.667L185.501 118.667L185.509 118.667C187.965 118.695 190.408 118.284 192.732 117.451C194.836 116.7 196.754 115.464 198.345 113.833C199.935 112.202 201.157 110.217 201.919 108.023C202.823 105.399 203.262 102.625 203.214 99.8355L203.214 99.8288V63.6036H189.186V98.4958C189.3 100.22 189.047 101.95 188.446 103.562L188.421 103.628ZM188.405 62.8218H203.995V99.822C204.045 102.702 203.592 105.567 202.658 108.278C201.86 110.579 200.577 112.664 198.905 114.379C197.232 116.094 195.213 117.395 192.996 118.187C190.585 119.051 188.05 119.478 185.5 119.449C182.939 119.476 180.392 119.05 177.968 118.187C175.763 117.398 173.75 116.116 172.067 114.431C170.387 112.692 169.083 110.597 168.241 108.287C167.281 105.58 166.815 102.709 166.867 99.822V62.8218H182.412V98.5219C182.298 100.144 182.554 101.771 183.159 103.27C183.408 103.664 183.757 103.979 184.167 104.179C184.577 104.38 185.032 104.458 185.482 104.406C185.917 104.462 186.359 104.386 186.754 104.188C187.149 103.99 187.482 103.678 187.713 103.289C188.279 101.773 188.515 100.145 188.405 98.5219V62.8218Z"
              fill="currentColor" />
      </svg>
    </div>
  );
}
